/**
 * Keep article citations structurally separate from editable Markdown so they always
 * remain ordered, accessible, and anchored at the end of an article.
 */

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import type { SxProps, Theme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { useId, useState } from "react";

/**
 * The async clipboard API needs a secure context and a user gesture, and some WebViews
 * still reject it quietly even with both; fall back to the legacy selection-based copy
 * before giving up. Only a `true` return may claim the text was copied.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fall through to the legacy path.
  }
  const holder = document.createElement("textarea");
  holder.value = text;
  holder.setAttribute("readonly", "");
  holder.style.position = "fixed";
  holder.style.opacity = "0";
  document.body.appendChild(holder);
  holder.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    holder.remove();
  }
}

/**
 * The scientific references attached to an article.
 *
 * References are structured data rather than Markdown in the body, so this component
 * owns their position and semantics in both the member article and the admin preview.
 * Citations are intentionally plain text: the source material contains no verified
 * URLs, and inventing links from citation text would be misleading.
 */
export function ArticleReferences({
  heading,
  references,
  copy,
}: {
  heading: string;
  references: readonly string[];
  /**
   * When present, tapping a citation copies it and confirms with a toast. The strings
   * arrive translated because the ui package holds no catalogue; `snackbarSx` exists
   * so the member app can position the toast clear of its own chrome.
   */
  copy?: {
    label: string;
    message: string;
    snackbarSx?: SxProps<Theme>;
  };
}) {
  const headingId = useId();
  const [copied, setCopied] = useState(false);

  if (references.length === 0) return null;

  const copyReference = async (reference: string) => {
    if (await copyText(reference)) setCopied(true);
  };

  return (
    <Box
      component="section"
      aria-labelledby={headingId}
      data-testid="article.references"
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      <Divider />
      <Typography
        id={headingId}
        component="h2"
        variant="H3"
        sx={{ color: "text.primary", marginTop: 1 }}
      >
        {heading}
      </Typography>
      <Box
        component="ol"
        sx={{
          margin: 0,
          paddingLeft: 3,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {references.map((reference, index) => (
          <Typography
            key={`${index}:${reference}`}
            component="li"
            variant="T2"
            sx={{ color: "text.muted", paddingLeft: 0.5 }}
          >
            {copy ? (
              <ButtonBase
                onClick={() => void copyReference(reference)}
                title={copy.label}
                data-testid="article.reference-copy"
                sx={{
                  font: "inherit",
                  color: "inherit",
                  textAlign: "left",
                }}
              >
                {reference}
              </ButtonBase>
            ) : (
              reference
            )}
          </Typography>
        ))}
      </Box>

      {copy ? (
        <Snackbar
          open={copied}
          autoHideDuration={3000}
          onClose={() => setCopied(false)}
          message={copy.message}
          sx={copy.snackbarSx}
          data-testid="article.reference-copied"
        />
      ) : null}
    </Box>
  );
}
